import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../components/layout/Layout';
import api from '../services/api';
import { fmtDateTime } from '../utils/helpers';
import {useTranslation} from 'react-i18next';

const DEVICE_ICON: Record<string, string> = { DESKTOP: '🖥️', MOBILE: '📱', TABLET: '📱', WEB: '🌐' };

export default function SessionsPage() {
  const {t}=useTranslation();
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [busyAll, setBusyAll] = useState(false);

  const notify = (m: string) => { setMsg(m); setTimeout(() => setMsg(''), 4000); };

  const load = useCallback(() => {
    setLoading(true);
    api.getMySessions().then(r => setSessions(r.data || [])).catch(() => notify('Failed to load sessions')).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const terminate = async (id: string) => {
    if (!window.confirm('Sign out this device?')) return;
    setBusyId(id);
    try {
      await api.invalidateMySession(id);
      notify('Session terminated');
      load();
    } catch {
      notify('Failed to terminate session');
    } finally {
      setBusyId(null);
    }
  };

  const terminateOthers = async () => {
    if (!window.confirm('Sign out all other devices? Your current session stays active.')) return;
    setBusyAll(true);
    try {
      await api.logoutOtherSessions();
      notify('All other sessions terminated');
      load();
    } catch {
      notify('Failed to terminate sessions');
    } finally {
      setBusyAll(false);
    }
  };

  return (
    <Layout title="Active Sessions">
      <div className="max-w-4xl mx-auto">
        {msg && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-xl text-sm">{msg}</div>
        )}

        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Active Sessions</h2>
            <p className="text-sm text-gray-500">Devices currently signed in to your account. Terminate any you don't recognize.</p>
          </div>
          <button
            onClick={terminateOthers}
            disabled={busyAll || sessions.filter(s => !s.current).length === 0}
            className="px-4 py-2 rounded-xl border border-red-200 text-red-600 text-sm font-semibold hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busyAll ? 'Signing out…' : 'Sign out all other devices'}
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-400 text-sm">{t('common.loading','Loading…')}</div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm bg-white rounded-2xl border border-gray-100">No active sessions found.</div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100 overflow-hidden">
            {sessions.map((s) => (
              <div key={s.id} className="flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-2xl">{DEVICE_ICON[s.deviceType?.toUpperCase()] || '💻'}</span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-gray-800 truncate">
                        {s.browser || 'Unknown browser'} · {s.os || 'Unknown OS'}
                      </p>
                      {s.current && (
                        <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[11px] font-semibold">This device</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {s.ipAddress || 'Unknown IP'} · Last active {fmtDateTime(s.lastActiveAt)}
                    </p>
                  </div>
                </div>
                {!s.current && (
                  <button
                    onClick={() => terminate(s.id)}
                    disabled={busyId === s.id}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold hover:border-red-200 hover:text-red-600 disabled:opacity-40 flex-shrink-0"
                  >
                    {busyId === s.id ? 'Signing out…' : 'Sign out'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
